import React from "react";
import { Link } from "react-router-dom";
import { FileSpreadsheet, FileJson, Download, ArrowRight } from "lucide-react";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";

const downloads = [
  {
    id: "csv",
    icon: FileSpreadsheet,
    title: "CMS-1500 claim form boxes list — CSV",
    desc: "Every box on the CMS-1500 (HCFA 1500) claim form as a flat list of field labels — including all Box 24 service-line sub-columns and header sub-fields — with the SQL data type for each field. Ready to open in Excel, Google Sheets, or use as a schema for a database or ETL pipeline.",
    href: "/downloads/cms-1500-claim-form-boxes-list.csv",
    filename: "cms-1500-claim-form-boxes-list.csv",
    meta: "CSV · 100+ fields · ~4 KB",
    testid: "download-cms1500-csv",
  },
  {
    id: "json",
    icon: FileJson,
    title: "CMS-1500 bounding boxes — JSON",
    desc: "Machine-readable bounding-box coordinates for every field on the CMS-1500 form. Field label plus x, y, width, height per box — ready to load into OCR training pipelines, dataset builders, form-annotation tools, or UI overlays that highlight fields on a scanned claim.",
    href: "/downloads/cms-1500-bounding-boxes.json",
    filename: "cms-1500-bounding-boxes.json",
    meta: "JSON · Field-level coords · ~42 KB",
    testid: "download-cms1500-bounding-json",
  },
];

const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hewto.ai/" },
    { "@type": "ListItem", position: 2, name: "Resources", item: "https://www.hewto.ai/resources/" },
    { "@type": "ListItem", position: 3, name: "CMS-1500 Claim Form boxes list", item: "https://www.hewto.ai/resources/cms-1500-claim-form-boxes-list" },
  ],
};

const CMS1500Fields = () => (
  <PageTransition>
    <Seo
      title="CMS-1500 Claim Form Boxes List — Free CSV & Bounding-Box JSON Download | Hewto.ai"
      description="Free CMS-1500 claim form boxes list — a downloadable CSV of every box on the CMS-1500 (HCFA 1500) claim form with its data type, plus a machine-readable JSON of bounding-box coordinates for every field."
      keywords="cms 1500 claim form boxes list, cms 1500 box list, cms 1500 form boxes, cms 1500 form fields list, hcfa 1500 boxes list, cms 1500 form fields csv, cms 1500 bounding boxes, cms 1500 bounding box json, cms 1500 field coordinates, hcfa 1500 bounding boxes, cms 1500 ocr training dataset, cms-1500 form locators"
      canonical="https://www.hewto.ai/resources/cms-1500-claim-form-boxes-list"
      jsonLd={breadcrumbJsonLd}
    />

    <PageHero
      eyebrow="Resources · Free downloads"
      title="CMS-1500 Claim Form boxes list"
      subtitle="Two free downloads for the CMS-1500 (HCFA 1500) claim form — a full CSV list of every box and field with its data type, and machine-readable bounding-box coordinates for every field as JSON."
    />

    <nav aria-label="Breadcrumb" className="mx-auto max-w-7xl px-5 pt-6 text-sm text-slate-500 sm:px-8">
      <ol className="flex flex-wrap items-center gap-1.5">
        <li><Link to="/" className="hover:text-brand" data-testid="breadcrumb-home">Home</Link></li>
        <li className="text-slate-300">/</li>
        <li>Resources</li>
        <li className="text-slate-300">/</li>
        <li className="font-medium text-slate-700">CMS-1500 Claim Form boxes list</li>
      </ol>
    </nav>

    <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {downloads.map((d, i) => (
          <Reveal key={d.id} delay={i * 0.08}>
            <div className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-xl hover:shadow-brand/10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <d.icon className="h-6 w-6" />
              </span>
              <h2 className="mt-5 text-xl font-bold text-slate-900">{d.title}</h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{d.desc}</p>
              <p className="mt-4 font-mono text-xs text-slate-500">{d.meta}</p>
              <a
                href={d.href}
                download={d.filename}
                data-testid={d.testid}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
              >
                <Download className="h-4 w-4" /> Download
              </a>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-4xl px-5 pb-24 sm:px-8">
      <Reveal>
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-brand-50 via-white to-white p-8 sm:p-10">
          <h2 className="text-2xl font-bold text-slate-900">Automate the whole form</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            These references are useful — but if you handle real CMS-1500 claim volume, Hewto.ai reads every one of
            these boxes automatically (scanned, faxed, handwritten, or red drop-out ink) and generates a validated
            X12 837 Professional file end-to-end.
          </p>
          <Link
            to="/cms-1500-ocr"
            data-testid="link-cms1500-product"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
          >
            See CMS-1500 OCR software <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
    </section>

    <CTABand />
  </PageTransition>
);

export default CMS1500Fields;
